import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import './Home.css'; 
import Footer from './Footer'; 
import ProgramsSection from './ProgramsSection'; 

// --- Wave Divider ---
const WaveDivider = ({ flip, color }) => (
  <div className={`wave-section-divider ${flip ? 'flip' : ''}`}>
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
      <path fill={color || 'var(--bg-primary)'} d="M0,32L48,37.3C96,43,192,53,288,53.3C384,53,480,43,576,42.7C672,43,768,53,864,58.7C960,64,1056,64,1152,56C1248,48,1344,32,1392,24L1440,16L1440,80L1392,80C1344,80,1248,80,1152,80C1056,80,960,80,864,80C768,80,672,80,576,80C480,80,384,80,288,80C192,80,96,80,48,80L0,80Z" />
    </svg>
  </div>
);

// --- Animated Counter ---
const AnimatedCounter = ({ target, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const num = parseInt(target);
          const duration = 2000;
          const steps = 60;
          const increment = num / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= num) {
              setCount(num);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// --- Section Components ---

const AboutSection = () => (
  <section className="about-section section" id="about">
    <div className="section-inner">
      <span className="section-badge">🌍 Our Mission</span>
      <h2 className="section__title">About OceanSense</h2>
      <p className="section__desc">
        OceanSense is committed to raising awareness about ocean conservation. Our mission is to protect marine life, reduce pollution, and promote sustainable ocean practices for a healthier planet. We believe that every small action counts toward preserving the beauty and biodiversity of our oceans.
      </p>
      <div className="about-features">
        <div className="about-feature">
          <div className="af-icon-wrap rescue"><span>🐢</span></div>
          <h3>Wildlife Rescue</h3>
          <p>24/7 guidance for marine animal emergencies with direct helpline connections.</p>
        </div>
        <div className="about-feature">
          <div className="af-icon-wrap dashboard"><span>📊</span></div>
          <h3>Live Dashboard</h3>
          <p>Real-time coastal weather, ocean health data, and conservation insights.</p>
        </div>
        <div className="about-feature">
          <div className="af-icon-wrap ai"><span>🤖</span></div>
          <h3>AI Assistant</h3>
          <p>Ask any ocean question — powered by AI, available anytime on every page.</p>
        </div>
      </div>
    </div>
  </section>
);

const ImpactSection = () => {
  const [stats, setStats] = useState({ reports: 0, subscribers: 0, users: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [reportsSnap, subscribersSnap] = await Promise.all([
          getDocs(collection(db, 'reports')),
          getDocs(collection(db, 'subscribers')),
        ]);
        setStats({
          reports: reportsSnap.size,
          subscribers: subscribersSnap.size,
          users: reportsSnap.size + subscribersSnap.size,
        });
      } catch (err) {
        console.log('Stats fetch error:', err);
      }
    };
    fetchStats();
  }, []);

  return (
    <section className="impact-section section" id="impact">
      <div className="section-inner">
        <span className="section-badge">📊 Live Platform Stats</span>
        <h2 className="section__title">Our Impact</h2>
        <p className="section__subtitle">Real numbers from our platform — updated in real-time.</p>
        <div className="impact-stats">
          <div className="stat">
            <div className="stat-icon">🚨</div>
            <h3><AnimatedCounter target={String(stats.reports)} suffix="" /></h3>
            <p>Problems Reported</p>
            <span className="stat-live">● Live Data</span>
          </div>
          <div className="stat">
            <div className="stat-icon">📧</div>
            <h3><AnimatedCounter target={String(stats.subscribers)} suffix="" /></h3>
            <p>Newsletter Subscribers</p>
            <span className="stat-live">● Live Data</span>
          </div>
          <div className="stat">
            <div className="stat-icon">👥</div>
            <h3><AnimatedCounter target={String(stats.users)} suffix="" /></h3>
            <p>Community Members</p>
            <span className="stat-live">● Live Data</span>
          </div>
          <div className="stat ai-stat">
            <div className="stat-icon">🤖</div>
            <h3>24/7</h3>
            <p>AI Assistant Available</p>
            <span className="stat-live online">● Always Online</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className="testimonials-section section" id="testimonials">
    <div className="section-inner">
      <span className="section-badge">💬 What People Say</span>
      <h2 className="section__title">Testimonials</h2>
      <div className="testimonials">
        <blockquote>
          <div className="quote-icon">"</div>
          <p className="quote-text">OceanSense has changed the way I think about ocean conservation. Their programs are impactful and life-changing.</p>
          <div className="quote-author">
            <div className="author-avatar">R</div>
            <div>
              <span className="author-name">Rishav</span>
              <span className="author-role">Volunteer</span>
            </div>
          </div>
        </blockquote>
        <blockquote>
          <div className="quote-icon">"</div>
          <p className="quote-text">Being part of this community has given me the chance to contribute to something truly meaningful.</p>
          <div className="quote-author">
            <div className="author-avatar">N</div>
            <div>
              <span className="author-name">Nihit</span>
              <span className="author-role">Program Participant</span>
            </div>
          </div>
        </blockquote>
        <blockquote>
          <div className="quote-icon">"</div>
          <p className="quote-text">The impact they've had on local communities and marine ecosystems is truly remarkable. Proud to support their mission.</p>
          <div className="quote-author">
            <div className="author-avatar">A</div>
            <div>
              <span className="author-name">Ananya</span>
              <span className="author-role">Donor</span>
            </div>
          </div>
        </blockquote>
      </div>
    </div>
  </section>
);

const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'events'));
        if (snapshot.size > 0) {
          const evts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setEvents(evts);
        } else {
          // Seed default events to Firestore if none exist
          const defaults = [
            { title: 'Beach Cleanup Drive', desc: 'Join us for a beach cleanup at Marina Beach. Bring friends, make an impact!', day: '07', month: 'JUN', location: '📍 Chennai, India' },
            { title: 'Marine Life Documentary', desc: 'Online event showcasing the beauty and challenges of marine ecosystems.', day: '15', month: 'JUN', location: '💻 Online Event' },
            { title: 'Coral Reef Workshop', desc: 'Hands-on workshop learning about coral reef ecosystems and restoration techniques.', day: '22', month: 'JUL', location: '🏝️ Goa, India' },
          ];
          for (const evt of defaults) {
            await addDoc(collection(db, 'events'), { ...evt, createdAt: serverTimestamp() });
          }
          setEvents(defaults);
        }
      } catch (err) {
        // Fallback if Firebase fails
        setEvents([
          { title: 'Beach Cleanup Drive', desc: 'Join us for a beach cleanup at Marina Beach.', day: '07', month: 'JUN', location: '📍 Chennai, India' },
          { title: 'Marine Life Documentary', desc: 'Online event showcasing marine ecosystems.', day: '15', month: 'JUN', location: '💻 Online Event' },
          { title: 'Coral Reef Workshop', desc: 'Hands-on workshop about coral reef restoration.', day: '22', month: 'JUL', location: '🏝️ Goa, India' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <section className="events-section section" id="events">
      <div className="section-inner">
        <span className="section-badge">📅 Get Involved</span>
        <h2 className="section__title">Upcoming Events</h2>
        {loading ? (
          <p style={{ color: 'var(--gray-400)' }}>Loading events...</p>
        ) : (
          <div className="events">
            {events.map((evt, i) => (
              <div key={evt.id || i} className="event">
                <div className="event-date">
                  <span className="event-day">{evt.day}</span>
                  <span className="event-month">{evt.month}</span>
                </div>
                <div className="event-info">
                  <h3>{evt.title}</h3>
                  <p>{evt.desc}</p>
                  <span className="event-tag">{evt.location}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};



const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: 'How can I volunteer with OceanSense?', a: "Fill out the form on our 'Join a Program' page, and we'll get back to you with details about upcoming volunteer opportunities in your area." },
    { q: 'Where do donations go?', a: 'All donations go directly toward our marine conservation projects and outreach programs. We are fully transparent with our financials.' },
    { q: 'Do I need experience to participate?', a: 'No prior experience is needed! We provide full training for all our programs. All you need is passion for the ocean.' },
    { q: 'Can I organize a local cleanup event?', a: "Absolutely! Reach out through our Forum or Contact page and we'll help you get set up with resources and support." },
  ];

  return (
    <section className="faq-section section" id="faq">
      <div className="section-inner">
        <span className="section-badge">❓ Help Center</span>
        <h2 className="section__title">Frequently Asked Questions</h2>
        <div className="faqs">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className={`faq-item ${openIndex === i ? 'open' : ''}`}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <div className="faq-question">
                <h3>{faq.q}</h3>
                <span className="faq-toggle">{openIndex === i ? '−' : '+'}</span>
              </div>
              <div className="faq-answer">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'subscribers'), {
        email: email,
        subscribedAt: serverTimestamp(),
        source: 'homepage_newsletter',
      });
      setSubscribed(true);
      setEmail('');
    } catch (err) {
      console.error('Newsletter save error:', err);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="newsletter-section section">
      <div className="section-inner">
        <div className="newsletter-content">
          <h2>Stay in the Loop</h2>
          <p>Get ocean conservation news, event updates, and tips delivered to your inbox.</p>
          {subscribed ? (
            <div className="newsletter-success">
              <span>✅</span> Thanks for subscribing! Your email has been saved.
            </div>
          ) : (
            <form className="newsletter-form" onSubmit={handleSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={saving}
              />
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Subscribe'}
              </button>
            </form>
          )}
          {error && <p style={{ color: 'var(--error)', marginTop: '8px', fontSize: '0.85rem' }}>{error}</p>}
        </div>
      </div>
    </section>
  );
};

// --- Quick Help Banner ---
const QuickHelpBanner = () => {
  const navigate = useNavigate();
  return (
    <section className="quick-help-section section">
      <div className="section-inner">
        <span className="section-badge">🆘 Need Help?</span>
        <h2 className="section__title">How Can We Help You?</h2>
        <p className="section__subtitle">OceanSense is more than awareness — we solve real problems.</p>
        <div className="quick-help-grid">
          <div className="quick-help-card" onClick={() => navigate('/report')}>
            <span className="qh-icon">🐢</span>
            <h3>Found a Stranded Animal?</h3>
            <p>Get step-by-step rescue instructions and emergency helpline numbers.</p>
            <span className="qh-link">Report Now →</span>
          </div>
          <div className="quick-help-card" onClick={() => navigate('/report')}>
            <span className="qh-icon">🏭</span>
            <h3>Report Pollution</h3>
            <p>Report oil spills, plastic dumping, or chemical discharge to authorities.</p>
            <span className="qh-link">Report Now →</span>
          </div>
          <div className="quick-help-card" onClick={() => navigate('/dashboard')}>
            <span className="qh-icon">🌊</span>
            <h3>Check Coastal Weather</h3>
            <p>Live weather data, wave height, and safety info for 6 coastal cities.</p>
            <span className="qh-link">View Dashboard →</span>
          </div>
          <div className="quick-help-card" onClick={() => navigate('/emergency')}>
            <span className="qh-icon">📞</span>
            <h3>Emergency Contacts</h3>
            <p>Coast Guard, Wildlife SOS, NDMA and 20+ helplines at your fingertips.</p>
            <span className="qh-link">View Directory →</span>
          </div>
        </div>
      </div>
    </section>
  );
};


// --- Main Home Component ---

const Home = () => {
  const navigate = useNavigate();

  return (
    <HelmetProvider>
      <div id="home">
        <Helmet>
          <title>OceanSense — Protect Our Oceans for Future Generations</title>
          <meta name="description" content="OceanSense is committed to raising awareness about ocean conservation. Join us in protecting marine life, reducing pollution, and promoting sustainable ocean practices." />
        </Helmet>
       
        <section className="hero-section">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline 
            id="background-video"
            onError={(e) => { e.target.style.display = 'none'; }}
          >
            <source src="https://cdn.pixabay.com/video/2022/08/19/128421-741495470_large.mp4" type="video/mp4" />
            <source src="https://firebasestorage.googleapis.com/v0/b/ocean-conservation-541d0.appspot.com/o/background-video.mp4?alt=media&token=85688a4d-3add-4916-94be-e0cf12aac154" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <span className="hero-badge">🌊 Ocean Conservation Platform</span>
            <h1>Protect Our Oceans<br/>for Future Generations</h1>
            <p>Join a global community dedicated to safeguarding the beauty and biodiversity of our oceans. Every action counts.</p>
            <div className="hero-buttons">
              <button className="hero-cta primary" onClick={() => navigate('/fish-details')}>
                Explore Marine Life
              </button>
              <button className="hero-cta secondary" onClick={() => navigate('/programs')}>
                Our Programs
              </button>
            </div>
            <div className="hero-floating-stats">
              <div className="hero-stat-pill">
                <span className="hsp-dot live"></span>
                <span>429 ppm CO₂</span>
              </div>
              <div className="hero-stat-pill">
                <span className="hsp-dot active"></span>
                <span>6 Cities Monitored</span>
              </div>
              <div className="hero-stat-pill">
                <span className="hsp-dot online"></span>
                <span>AI Assistant Online</span>
              </div>
            </div>
          </div>
          <div className="hero-scroll-indicator">
            <span>Scroll to explore</span>
            <div className="scroll-arrow">↓</div>
          </div>
        </section>

        <WaveDivider color="var(--bg-warm)" />
        <AboutSection />

        <WaveDivider color="var(--bg-primary)" flip />
        <QuickHelpBanner />

        <WaveDivider color="var(--bg-warm)" />
        <ProgramsSection />

        <WaveDivider color="var(--bg-primary)" flip />
        <ImpactSection />

        <WaveDivider color="#fff" />
        <TestimonialsSection />

        <WaveDivider color="var(--bg-warm)" flip />
        <EventsSection />

        <WaveDivider color="#fff" />
        <FaqSection />

        <NewsletterSection />

        <Footer />
      </div>
    </HelmetProvider>
  );
};

export default Home;
